import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, BookOpen, Sun, Moon } from 'lucide-react';
import { Link } from 'react-router-dom';
import MDEditor from '@uiw/react-md-editor';
import '@uiw/react-md-editor/markdown-editor.css';
import '@uiw/react-markdown-preview/markdown.css';
import rehypeSlug from 'rehype-slug';
import rehypeAutolinkHeadings from 'rehype-autolink-headings';

// Custom Styles
const customStyles = `
  .custom-scrollbar::-webkit-scrollbar { width: 6px; }
  .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
  .custom-scrollbar::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 3px; }
  .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: #94a3b8; }
  .dark .custom-scrollbar::-webkit-scrollbar-thumb { background: #475569; }
  .dark .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: #64748b; }
  
  /* Professional typography */
  .prose {
    font-family: 'Source Serif 4', Georgia, Cambria, 'Times New Roman', Times, serif !important;
    line-height: 1.75 !important;
    letter-spacing: 0.025em !important;
  }
  
  .prose h1, .prose h2, .prose h3, .prose h4, .prose h5, .prose h6 {
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif !important;
    font-weight: 600 !important;
    letter-spacing: -0.025em !important;
  }
  
  .prose p {
    font-size: 1.125rem !important;
    line-height: 1.8 !important;
    margin-bottom: 1.5rem !important;
  }
  
  /* Fix code block styling for light mode */
  .w-md-editor-text-container .w-md-editor-text pre,
  .w-md-editor-text-container .w-md-editor-text code {
    background-color: #f8fafc !important;
    color: #1e293b !important;
  }
  
  .dark .w-md-editor-text-container .w-md-editor-text pre,
  .dark .w-md-editor-text-container .w-md-editor-text code {
    background-color: #1e293b !important;
    color: #e2e8f0 !important;
  }
  
  /* Override prose code styling */
  .prose code {
    background-color: #f1f5f9 !important;
    color: #475569 !important;
    padding: 0.125rem 0.25rem !important;
    border-radius: 0.25rem !important;
    font-size: 0.875em !important;
  }
  
  .prose pre {
    background-color: #f8fafc !important;
    color: #1e293b !important;
    border: 1px solid #e2e8f0 !important;
    padding: 1.5rem !important;
    border-radius: 0.5rem !important;
  }
  
  .prose pre code {
    background-color: transparent !important;
    color: inherit !important;
    padding: 0 !important;
  }
  
  .dark .prose code {
    background-color: #1e293b !important;
    color: #cbd5e1 !important;
  }
  
  .dark .prose pre {
    background-color: #1e293b !important;
    color: #e2e8f0 !important;
    border: 1px solid #475569 !important;
    padding: 1.5rem !important;
    border-radius: 0.5rem !important;
  }
`;

const ResearchPage = ({ darkMode, toggleDarkMode }) => {
    const [markdown, setMarkdown] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const METRICS = {
        codeRequests: 847,
        aiTools: 3,
        errorReduction: 76,
        weeksTesting: 7,
    };


    // Fetch Markdown
    useEffect(() => {
        const fetchMarkdown = async () => {
            try {
                const res = await fetch('/research_paper.md');
                if (!res.ok) throw new Error('Failed to load research paper');
                const text = await res.text();
                setMarkdown(text);
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };
        fetchMarkdown();
    }, []);



    return (
        <div className="min-h-screen bg-white dark:bg-gray-950 transition-colors duration-200 relative">
            <style>{customStyles}</style>

            {/* Header */}
            <header className="bg-white/95 dark:bg-black/95 backdrop-blur-md border-b border-gray-200 dark:border-gray-900 sticky top-0 z-50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center h-16">
                    <Link
                        to="/"
                        className="flex items-center space-x-3 text-gray-900 dark:text-white hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                    >
                        <ArrowLeft size={20} />
                        <span className="font-medium">Back to Portfolio</span>
                    </Link>
                    <div className="flex items-center space-x-2">
                        <BookOpen className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                        <h1 className="text-xl font-bold text-gray-900 dark:text-white">Research</h1>
                    </div>
                </div>
            </header>


            {/* Dark Mode Toggle */}
            <motion.button
                className="fixed bottom-6 right-6 z-50 p-3 rounded-full bg-white dark:bg-gray-900 shadow-lg border hover:scale-105 transition"
                onClick={toggleDarkMode}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3 }}
            >
                {darkMode ? <Sun size={24} /> : <Moon size={24} />}
            </motion.button>

            {/* Hero Section */}
            <section className="py-20 bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 dark:from-gray-900 dark:via-blue-900/20 dark:to-purple-900/20">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, ease: 'easeOut' }}>
                        <div className="flex items-center justify-center mb-4">
                            <div className="flex items-center space-x-3 px-4 py-2 bg-green-100 dark:bg-green-900/30 rounded-full">
                                <BookOpen className="h-5 w-5 text-green-600 dark:text-green-400" />
                                <span className="text-green-700 dark:text-green-300 font-medium text-sm">Completed Research</span>
                            </div>
                        </div>
                        <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 dark:text-white mb-6">
                            PROJECT AIGES
                        </h1>
                        <p className="text-xl md:text-2xl text-gray-700 dark:text-gray-300 max-w-4xl mx-auto mb-8">
                            Foundational Research Toward Context-Aware AI Coding Systems
                        </p>
                        <div className="flex flex-wrap justify-center gap-4 text-sm text-gray-600 dark:text-gray-400 mb-12">
                            <div className="flex items-center space-x-2">
                                <BookOpen size={16} />
                                <span>September 2025</span>
                            </div>
                            <div className="flex items-center space-x-2">
                                <span>Authors: Utsav Mistry, Nishit Somani</span>
                            </div>
                        </div>

                        {/* Animated Metrics */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto">
                            <motion.div
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.4, delay: 0.1, ease: 'easeOut' }}
                                className="text-center p-6 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-xl border border-gray-200 dark:border-gray-700 shadow-lg"
                            >
                                <div className="text-3xl md:text-4xl font-bold text-blue-600 dark:text-blue-400 mb-2">
                                    {METRICS.codeRequests.toLocaleString()}
                                </div>
                                <div className="text-sm font-medium text-gray-600 dark:text-gray-400">Code Requests</div>
                                <div className="text-xs text-gray-500 dark:text-gray-500 mt-1">Analyzed</div>
                            </motion.div>

                            <motion.div
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.4, delay: 0.2, ease: 'easeOut' }}
                                className="text-center p-6 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-xl border border-gray-200 dark:border-gray-700 shadow-lg"
                            >
                                <div className="text-3xl md:text-4xl font-bold text-green-600 dark:text-green-400 mb-2">
                                    {METRICS.aiTools}
                                </div>
                                <div className="text-sm font-medium text-gray-600 dark:text-gray-400">AI Tools</div>
                                <div className="text-xs text-gray-500 dark:text-gray-500 mt-1">Tested</div>
                            </motion.div>

                            <motion.div
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.4, delay: 0.3, ease: 'easeOut' }}
                                className="text-center p-6 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-xl border border-gray-200 dark:border-gray-700 shadow-lg"
                            >
                                <div className="text-3xl md:text-4xl font-bold text-purple-600 dark:text-purple-400 mb-2">
                                    {METRICS.errorReduction}%
                                </div>
                                <div className="text-sm font-medium text-gray-600 dark:text-gray-400">Error Reduction</div>
                                <div className="text-xs text-gray-500 dark:text-gray-500 mt-1">Achieved</div>
                            </motion.div>

                            <motion.div
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.4, delay: 0.4, ease: 'easeOut' }}
                                className="text-center p-6 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-xl border border-gray-200 dark:border-gray-700 shadow-lg"
                            >
                                <div className="text-3xl md:text-4xl font-bold text-orange-600 dark:text-orange-400 mb-2">
                                    {METRICS.weeksTesting}
                                </div>
                                <div className="text-sm font-medium text-gray-600 dark:text-gray-400">Weeks Testing</div>
                                <div className="text-xs text-gray-500 dark:text-gray-500 mt-1">Finished</div>
                            </motion.div>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* Research Paper Content */}
            <section className="py-12">
                <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12">
                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 0.15, ease: 'easeOut' }}
                        className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-800 overflow-hidden backdrop-blur-sm"
                    >
                        <div className="p-6 md:p-10 lg:p-12">
                            <div className="flex items-center justify-between mb-6 pb-4 border-b border-gray-200 dark:border-gray-800">
                                <div className="text-left">
                                    <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 tracking-tight">Research Paper</h2>
                                    <p className="text-sm text-gray-500 dark:text-gray-400">Project AIGES · Completed study</p>
                                </div>
                                <div className="hidden sm:inline-flex items-center px-3 py-1 rounded-full bg-emerald-50 dark:bg-emerald-900/30 border border-emerald-200/70 dark:border-emerald-700/60 text-xs font-medium text-emerald-700 dark:text-emerald-300">
                                    Optimized reading view
                                </div>
                            </div>
                            {loading ? (
                                <div className="flex items-center justify-center h-64">
                                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                                </div>
                            ) : error ? (
                                <div className="text-center py-16">
                                    <p className="text-red-500 dark:text-red-400 text-lg">{error}</p>
                                </div>
                            ) : (
                                <div
                                    className="prose prose-xl dark:prose-invert max-w-none prose-headings:scroll-mt-20 max-h-[70vh] overflow-y-auto custom-scrollbar pr-2"
                                    data-color-mode={darkMode ? 'dark' : 'light'}
                                    style={{
                                        fontFamily: '"Source Serif 4", Georgia, Cambria, "Times New Roman", Times, serif',
                                        lineHeight: '1.75',
                                        letterSpacing: '0.025em'
                                    }}
                                >
                                    <MDEditor.Markdown
                                        source={markdown}
                                        style={{
                                            backgroundColor: 'transparent',
                                            color: darkMode ? '#e2e8f0' : '#1e293b'
                                        }}
                                        rehypePlugins={[
                                            rehypeSlug,
                                            [rehypeAutolinkHeadings, { behavior: 'wrap' }]
                                        ]}
                                        data-color-mode={darkMode ? 'dark' : 'light'}
                                    />
                                </div>
                            )}
                        </div>
                    </motion.div>
                </div>
            </section>


        </div>
    );
};

export default ResearchPage;
